import React, { useEffect, useState } from "react";

// Utils
import { useForm, Controller } from "react-hook-form";

// Redux
import { useSelector, useDispatch } from "react-redux";
import { fetchPaymentMethods } from "../../../redux/features/sales/paymentMethodsSlice";

// Routing
import { Link as RouterLink } from "react-router-dom";

// Styling
import { makeStyles } from "@material-ui/core/styles";

// Components
import { injectStripe } from "react-stripe-elements";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCVCElement,
} from "react-stripe-elements";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import InputAdornment from "@material-ui/core/InputAdornment";
import Divider from "@material-ui/core/Divider";
import Box from "@material-ui/core/Box";
import NumberFormatCustom from "../../FormFields/NumberFormatCustom";
import FullContainerProgress from "../../UI/FullContainerProgress";
import PaymentMethodsComboBox from "../../FormFields/PaymentMethodsComboBox";
import AddressComboBox from "../../FormFields/CustomerAddressComboBox";
import StripeElementWrapper from "../../FormFields/Stripe/StripeElementWrapper";
import MuiAlert from "@material-ui/lab/Alert";
import Link from "@material-ui/core/Link";

// Icons
import EditIcon from "@material-ui/icons/Edit";

//Styling
const useStyles = makeStyles((theme) => ({
  paperContainer: {
    padding: theme.spacing(3),
    position: "relative",
    minHeight: "100px",
    height: "100%",
  },
  form: {
    "& .MuiFormControl-root": {
      minWidth: 250,
    },
  },
  maxWidth: {
    minWidth: 120,
    width: "100%",
  },
  alert: {
    marginTop: theme.spacing(1),
    width: "100%",
  },
  dialogContent: { overflow: "visible", paddingBottom: theme.spacing(5) },
}));

const LinkRouter = (props) => <Link {...props} component={RouterLink} />;

const ChargeCreateDialog = ({
  stripe,
  currentPageOrder,
  open,
  onClose,
  onSubmit,
  readOnly = false,
}) => {
  //Styles
  const classes = useStyles();
  // Hooks
  const { handleSubmit, control, setValue } = useForm();

  const { customer_id, customer_name, id, amounts } = currentPageOrder;

  const remainingBalance = amounts
    ? Math.round((amounts?.post_discount_total - amounts?.totalPaid) * 100) /
      100
    : 0;

  // Redux
  const dispatch = useDispatch();
  const { isLoading: isLoadingCharges } = useSelector((state) => state.charges);

  // Local State — payment method mode
  const [paymentMode, setPaymentMode] = useState("select"); // "select" | "create"
  const [stripeError, setStripeError] = useState(null);

  // New payment method — address mode
  const [addressMode, setAddressMode] = useState("select"); // "select" | "create"
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [newLine1, setNewLine1] = useState("");
  const [newLine2, setNewLine2] = useState("");
  const [newCity, setNewCity] = useState("");
  const [newState, setNewState] = useState("");
  const [newZip, setNewZip] = useState("");
  const [newCountry, setNewCountry] = useState("");

  useEffect(() => {
    if (customer_id) dispatch(fetchPaymentMethods(customer_id));
  }, [customer_id, dispatch]);

  const handlePayRemainingBalance = () => {
    setValue("amount", String(remainingBalance));
  };

  const isNewPaymentMethodReady = () => {
    if (addressMode === "select") return selectedAddress !== null;
    return !!(newLine1 && newCity && newState && newZip && newCountry);
  };

  const getAddressForStripe = () => {
    if (addressMode === "create") {
      return {
        line_1: newLine1,
        line_2: newLine2,
        city: newCity,
        state: newState,
        zip: newZip,
        country: newCountry,
      };
    }
    return selectedAddress;
  };

  const formSubmit = async (data) => {
    const { amount, paymentMethod } = data;

    if (!amount) return;

    if (paymentMode === "select") {
      if (!paymentMethod) return;
      onSubmit({
        order_id: id,
        amount: Number(amount),
        card_id: paymentMethod.id,
      });
      return;
    }

    // Create new payment method flow
    const addr = getAddressForStripe();
    if (!addr) return;

    const clientData = {
      name: customer_name,
      address_line1: addr.line_1,
      address_line2: addr.line_2,
      address_city: addr.city,
      address_state: addr.state,
      address_zip: addr.zip,
      address_country: addr.country,
    };

    const stripeRes = await stripe.createPaymentMethod("card", {
      billing_details: {
        name: clientData.name,
        address: {
          line1: clientData.address_line1,
          line2: clientData.address_line2,
          city: clientData.address_city,
          state: clientData.address_state,
          postal_code: clientData.address_zip,
          country: clientData.address_country,
        },
      },
    });
    if (stripeRes.error) {
      setStripeError(stripeRes.error.message);
      return;
    }

    setStripeError(null);
    const payload = {
      order_id: id,
      amount: Number(amount),
      newPaymentMethod: {
        payment_method_id: stripeRes.paymentMethod.id,
      },
    };

    if (addressMode === "create") {
      payload.newPaymentMethod.newAddress = {
        line_1: newLine1,
        line_2: newLine2 || "",
        city: newCity,
        state: newState,
        zip: newZip + "",
        country: newCountry,
        others_details: "",
      };
    }

    onSubmit(payload);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      aria-labelledby="alert-charge-view"
      aria-describedby="alert-charge-view"
      scroll="body"
    >
      {isLoadingCharges ? (
        <>
          <DialogContent>
            <Box width={700} height={500}>
              <FullContainerProgress />
            </Box>
          </DialogContent>
        </>
      ) : (
        <>
          <DialogTitle id="alert-charge-view">
            Create Charge for order {id}
          </DialogTitle>

          <form className={classes.form} onSubmit={handleSubmit(formSubmit)}>
            <DialogContent className={classes.dialogContent}>
              <Grid container spacing={3}>
                <>
                  <Grid item xs={12}>
                    <Typography variant="body1" component="p">
                      Customer Name: {customer_name}
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body1" component="p">
                      <LinkRouter to={`/sales/customers/${customer_id}`}>
                        <EditIcon fontSize="small" /> Edit Customer Address /
                        Payment Methods
                      </LinkRouter>
                    </Typography>
                  </Grid>

                  {stripeError && (
                    <Grid item xs={12}>
                      <MuiAlert className={classes.alert} severity="error">
                        {stripeError}
                      </MuiAlert>
                    </Grid>
                  )}

                  {/* Amount */}
                  <Grid item xs={12}>
                    <Controller
                      name="amount"
                      control={control}
                      defaultValue="0"
                      render={({ onChange, value }) => (
                        <TextField
                          className={classes.maxWidth}
                          style={{ flex: 1 }}
                          required
                          label="Amount"
                          value={value}
                          onChange={(e) => onChange(e?.target?.value ?? e)}
                          InputProps={{
                            readOnly,
                            inputComponent: NumberFormatCustom,
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            ),
                            endAdornment: !readOnly && (
                              <InputAdornment position="end">
                                <Button
                                  onClick={handlePayRemainingBalance}
                                  size="small"
                                  color="secondary"
                                >
                                  Pay Remaining Balance
                                </Button>
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Payment Method section */}
                  <Grid item xs={12}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      mb={1}
                    >
                      <Typography variant="body2" color="textSecondary">
                        Payment Method
                      </Typography>
                      {!readOnly && (
                        <ButtonGroup size="small" variant="outlined">
                          <Button
                            color={
                              paymentMode === "select" ? "primary" : "default"
                            }
                            variant={
                              paymentMode === "select"
                                ? "contained"
                                : "outlined"
                            }
                            onClick={() => setPaymentMode("select")}
                          >
                            Select Existing
                          </Button>
                          <Button
                            color={
                              paymentMode === "create" ? "primary" : "default"
                            }
                            variant={
                              paymentMode === "create"
                                ? "contained"
                                : "outlined"
                            }
                            onClick={() => setPaymentMode("create")}
                          >
                            Create New
                          </Button>
                        </ButtonGroup>
                      )}
                    </Box>
                    <Divider style={{ marginBottom: 16 }} />

                    {paymentMode === "select" ? (
                      <Controller
                        name="paymentMethod"
                        control={control}
                        render={(props) => (
                          <PaymentMethodsComboBox
                            customer_id={customer_id}
                            defaultValue={props.value}
                            onChange={props.onChange}
                            readOnly={readOnly}
                          />
                        )}
                      />
                    ) : (
                      <Grid container spacing={2}>
                        {/* Card fields */}
                        <Grid item xs={12}>
                          <StripeElementWrapper
                            label="Card Number"
                            component={CardNumberElement}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <StripeElementWrapper
                            label="Expires"
                            component={CardExpiryElement}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <StripeElementWrapper
                            label="CVC"
                            component={CardCVCElement}
                          />
                        </Grid>

                        {/* Billing address sub-section */}
                        <Grid item xs={12}>
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={1}
                          >
                            <Typography variant="body2" color="textSecondary">
                              Billing Address
                            </Typography>
                            <ButtonGroup size="small" variant="outlined">
                              <Button
                                color={
                                  addressMode === "select"
                                    ? "primary"
                                    : "default"
                                }
                                variant={
                                  addressMode === "select"
                                    ? "contained"
                                    : "outlined"
                                }
                                onClick={() => setAddressMode("select")}
                              >
                                Select Existing
                              </Button>
                              <Button
                                color={
                                  addressMode === "create"
                                    ? "primary"
                                    : "default"
                                }
                                variant={
                                  addressMode === "create"
                                    ? "contained"
                                    : "outlined"
                                }
                                onClick={() => setAddressMode("create")}
                              >
                                Create New
                              </Button>
                            </ButtonGroup>
                          </Box>
                          <Divider style={{ marginBottom: 16 }} />

                          {addressMode === "select" ? (
                            <AddressComboBox
                              customerId={customer_id}
                              onChange={(val) => setSelectedAddress(val)}
                            />
                          ) : (
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  label="Address Line 1"
                                  fullWidth
                                  required
                                  value={newLine1}
                                  onChange={(e) => setNewLine1(e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <TextField
                                  label="Address Line 2"
                                  fullWidth
                                  value={newLine2}
                                  onChange={(e) => setNewLine2(e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} md={6} lg={3}>
                                <TextField
                                  label="City"
                                  fullWidth
                                  required
                                  value={newCity}
                                  onChange={(e) => setNewCity(e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} md={6} lg={3}>
                                <TextField
                                  label="State"
                                  fullWidth
                                  required
                                  value={newState}
                                  onChange={(e) => setNewState(e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} md={6} lg={3}>
                                <TextField
                                  label="Zip"
                                  fullWidth
                                  required
                                  value={newZip}
                                  onChange={(e) => setNewZip(e.target.value)}
                                />
                              </Grid>
                              <Grid item xs={12} md={6} lg={3}>
                                <TextField
                                  label="Country"
                                  fullWidth
                                  required
                                  value={newCountry}
                                  onChange={(e) =>
                                    setNewCountry(e.target.value)
                                  }
                                />
                              </Grid>
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </>
              </Grid>
            </DialogContent>

            {!readOnly && (
              <DialogActions>
                <Button onClick={onClose} variant="outlined">
                  Cancel
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  type="submit"
                  disabled={
                    isLoadingCharges ||
                    (paymentMode === "create" && !isNewPaymentMethodReady())
                  }
                >
                  Create Charge
                </Button>
              </DialogActions>
            )}
          </form>
        </>
      )}
    </Dialog>
  );
};

export default injectStripe(ChargeCreateDialog);
